import $ from 'jquery'

// Minimal first-party replacement for the `jquery-form` package's `$.fn.ajaxForm`.
//
// The app only uses form-submit interception with the beforeSerialize / beforeSubmit hooks and a
// JSON $.ajax POST/PATCH (6 narrative/responsibilities/about-me forms; no file uploads, no other
// jquery-form methods). The upstream package ships as a hand-written UMD whose conditional
// `module.exports` Rolldown can't resolve, so it bundles to an empty module and the plugin is
// never installed. Implementing the small subset we need in-repo bundles cleanly and keeps the
// existing call-site API and $.ajax pipeline (CSRF, Django `_method`, etc.) unchanged.
//
// Importing this module installs $.fn.ajaxForm as a side effect.

export interface AjaxFormOptions {
    url: string
    /** Legacy alias for the HTTP method (jquery-form option). `method` wins if both are set. */
    type?: string
    method?: string
    dataType?: string
    /** Extra fields appended to the serialized form data. */
    data?: Record<string, unknown>
    beforeSerialize?: ($form: JQuery, options: AjaxFormOptions) => void
    /** Receives the serialized [{name, value}] array (may be mutated); return false to abort. */
    beforeSubmit?: (
        formData: Array<{ name: string; value: string }>,
        $form: JQuery,
        options: AjaxFormOptions
    ) => boolean | void
    success?: (response: unknown, textStatus: string, xhr: JQuery.jqXHR, $form: JQuery) => void
    error?: (xhr: JQuery.jqXHR, textStatus: string, errorThrown: string) => void
}

$.fn.ajaxForm = function (this: JQuery, options?: object): JQuery {
    const opts = (options ?? {}) as AjaxFormOptions
    return this.each(function () {
        const $form = $(this)
        // Namespaced so re-binding on a React re-render replaces the handler instead of stacking.
        $form.off('submit.owAjaxForm').on('submit.owAjaxForm', e => {
            e.preventDefault()
            opts.beforeSerialize?.($form, opts)
            const formData = $form.serializeArray()
            for (const [name, value] of Object.entries(opts.data ?? {})) {
                formData.push({ name, value: String(value) })
            }
            if (opts.beforeSubmit?.(formData, $form, opts) === false) {
                return
            }
            $.ajax({
                url: opts.url,
                type: opts.method ?? opts.type ?? 'POST',
                dataType: opts.dataType,
                data: $.param(formData),
            })
                .done((response: unknown, textStatus: string, xhr: JQuery.jqXHR) =>
                    opts.success?.(response, textStatus, xhr, $form)
                )
                .fail((xhr: JQuery.jqXHR, textStatus: string, errorThrown: string) =>
                    opts.error?.(xhr, textStatus, errorThrown)
                )
        })
    })
}
